/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useRef, useCallback } from 'react'
import { safeFetch } from '@utils/validateFetchUrl'

export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting'

interface UseConnectionStatusOptions {
  /** Optional callback when connection status changes */
  onStatusChange?: (status: ConnectionStatus) => void
  /** Interval in milliseconds for periodic connection checks (default: 30000) */
  checkInterval?: number
}

/**
 * Hook for monitoring connection status and detecting disconnections.
 *
 * Uses browser's native online/offline events as the primary method, with
 * minimal periodic checks as a fallback. Automatically detects when the
 * connection is lost or restored.
 *
 * @param options - Configuration options for the hook
 * @param options.onStatusChange - Optional callback when status changes
 * @param options.checkInterval - Interval for periodic checks in ms (default: 30000)
 *
 * @returns Object containing connection status and control functions
 * @returns status - Current connection status ('connected' | 'disconnected' | 'reconnecting')
 * @returns isConnected - Boolean indicating if currently connected
 * @returns isReconnecting - Boolean indicating if currently reconnecting
 * @returns reconnectAttempts - Number of reconnection attempts made
 * @returns forceReconnect - Function to force a reconnection check
 *
 * @example
 * ```tsx
 * const { status, isConnected, forceReconnect } = useConnectionStatus({
 *   onStatusChange: (status) => {
 *     logger.info('Connection status changed', { status });
 *   }
 * });
 *
 * if (!isConnected) {
 *   return <div>Connection lost. Reconnecting...</div>;
 * }
 * ```
 */
export function useConnectionStatus(options: UseConnectionStatusOptions = {}) {
  const { onStatusChange, checkInterval = 30000 } = options // Increased to 30 seconds
  const [status, setStatus] = useState<ConnectionStatus>('connected')
  const [reconnectAttempts, setReconnectAttempts] = useState(0)
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const statusRef = useRef<ConnectionStatus>('connected')
  const onStatusChangeRef = useRef(onStatusChange)

  // Keep refs in sync
  useEffect(() => {
    statusRef.current = status
  }, [status])

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange
  }, [onStatusChange])

  const updateStatus = useCallback((newStatus: ConnectionStatus) => {
    if (statusRef.current !== newStatus) {
      setStatus(newStatus)
      statusRef.current = newStatus
      onStatusChangeRef.current?.(newStatus)
    }
  }, [])

  const checkConnection = useCallback(async () => {
    // First check browser's online status
    if (!navigator.onLine) {
      if (statusRef.current !== 'disconnected') {
        updateStatus('disconnected')
      }
      return
    }

    // Only do a fetch check if we're not already connected or if we're reconnecting
    // This reduces unnecessary network requests
    if (statusRef.current === 'connected') {
      return // Skip fetch if already connected
    }

    try {
      // Use a lightweight health check - try to fetch the root with a very short timeout
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 1000) // Reduced timeout

      // Use a simple endpoint that's likely to be cached or very lightweight
      await safeFetch('/', {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-cache',
      })

      clearTimeout(timeoutId)

      const currentStatus = statusRef.current
      if (currentStatus !== 'connected') {
        setReconnectAttempts(0)
        updateStatus('connected')
      }
    } catch {
      // Only update status if we thought we were connected
      const currentStatus = statusRef.current
      if (currentStatus === 'connected') {
        updateStatus('disconnected')
      } else if (currentStatus === 'disconnected') {
        setReconnectAttempts(prev => prev + 1)
        updateStatus('reconnecting')
      }
    }
  }, [updateStatus])

  useEffect(() => {
    // Set initial status based on navigator.onLine
    if (!navigator.onLine) {
      updateStatus('disconnected')
    }

    // Check connection status periodically, but only when not connected
    // This reduces unnecessary checks when already connected
    const scheduleCheck = () => {
      // Clear any existing interval first
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }

      // Only set up periodic checks if we're not connected
      // When connected, rely on browser events
      if (statusRef.current !== 'connected') {
        checkIntervalRef.current = setInterval(checkConnection, checkInterval)
      }
    }

    scheduleCheck()

    // Listen to online/offline events - primary method
    const handleOnline = () => {
      setReconnectAttempts(0)
      updateStatus('connected')
      // Clear interval when online since we're connected
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }
    }

    const handleOffline = () => {
      updateStatus('disconnected')
      // Start periodic checks when offline
      scheduleCheck()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Initial check only if offline
    if (!navigator.onLine) {
      checkConnection()
    }

    // Cleanup function - ensure all intervals and event listeners are cleared
    return () => {
      // Clear interval
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }
      // Remove event listeners
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [checkConnection, checkInterval, updateStatus])

  const forceReconnect = useCallback(() => {
    setReconnectAttempts(0)
    checkConnection()
  }, [checkConnection])

  return {
    status,
    isConnected: status === 'connected',
    isReconnecting: status === 'reconnecting',
    reconnectAttempts,
    forceReconnect,
  }
}
